/**
 * components/CardVariantSelector.js - UI for selecting card variants
 * Location: /app/frontend/src/admin/page_builder/components/
 * 
 * Visual selector for choosing between card preset templates.
 */

import React from 'react';
import { 
  CardVariant, 
  CardVariantLabel, 
  CardVariantDescription,
  getCardVariantOptions,
} from '../cards/variants';
import { 
  BookOpen, 
  Search, 
  Star, 
  User,
} from 'lucide-react';
import { Label } from '../../../components/ui/label';

// Icon mapping for card variants
const CARD_ICONS = {
  [CardVariant.READING]: BookOpen,
  [CardVariant.DISCOVERY]: Search,
  [CardVariant.FEATURED]: Star,
  [CardVariant.CREATOR]: User,
};

/**
 * CardVariantSelector - Visual selector for card variants
 */
const CardVariantSelector = ({ value, onChange, showDescription = true }) => {
  const options = getCardVariantOptions();
  
  return (
    <div className="space-y-2">
      <Label className="text-zinc-300 text-sm">Card Variant</Label>
      <div className="grid grid-cols-2 gap-2">
        {options.map((option) => {
          const IconComponent = CARD_ICONS[option.value] || Search;
          const isSelected = value === option.value;
          
          return (
            <button
              key={option.value}
              onClick={() => onChange(option.value)}
              className={`
                p-3 rounded-lg border transition-all flex flex-col items-center gap-2 text-center
                ${isSelected 
                  ? 'border-orange-500 bg-orange-500/20 text-orange-400' 
                  : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
                }
              `}
              title={option.description}
            >
              <IconComponent className="w-5 h-5" />
              <span className="text-xs font-medium">
                {option.label.replace(' Card', '')}
              </span>
            </button>
          );
        })}
      </div>
      {showDescription && value && (
        <p className="text-xs text-zinc-500 mt-1">
          {CardVariantDescription[value]}
        </p>
      )}
    </div>
  );
};

/**
 * CardVariantPreview - Shows a preview of the selected card variant
 */
export const CardVariantPreview = ({ variant }) => {
  const IconComponent = CARD_ICONS[variant] || Search;
  
  // Mini preview based on variant
  const previewStyles = {
    [CardVariant.READING]: (
      <div className="flex items-center gap-2 bg-zinc-800 rounded p-2">
        <div className="w-8 h-10 bg-zinc-700 rounded" />
        <div className="flex-1">
          <div className="h-2 bg-zinc-600 rounded w-3/4 mb-1" />
          <div className="h-1.5 bg-zinc-700 rounded w-1/2" />
          <div className="h-1 bg-orange-500/50 rounded w-2/3 mt-2" />
        </div>
      </div>
    ),
    [CardVariant.DISCOVERY]: (
      <div className="bg-zinc-800 rounded overflow-hidden">
        <div className="w-full aspect-[2/3] bg-zinc-700" />
        <div className="p-2">
          <div className="h-2 bg-zinc-600 rounded w-3/4 mb-1" />
          <div className="h-1.5 bg-zinc-700 rounded w-1/2" />
        </div>
      </div>
    ),
    [CardVariant.FEATURED]: (
      <div className="bg-zinc-800 rounded-lg overflow-hidden">
        <div className="w-full aspect-video bg-zinc-700" />
        <div className="p-2">
          <div className="h-2 bg-zinc-600 rounded w-3/4 mb-1" />
          <div className="h-1.5 bg-zinc-700 rounded w-full mb-1" />
          <div className="h-1.5 bg-zinc-700 rounded w-2/3" />
        </div>
      </div>
    ),
    [CardVariant.CREATOR]: (
      <div className="bg-zinc-800 rounded-lg p-3 text-center">
        <div className="w-10 h-10 bg-zinc-700 rounded-full mx-auto mb-2" />
        <div className="h-2 bg-zinc-600 rounded w-2/3 mx-auto mb-1" />
        <div className="h-1.5 bg-zinc-700 rounded w-1/2 mx-auto" />
      </div>
    ),
  };
  
  return (
    <div className="mt-3">
      <Label className="text-zinc-400 text-xs">Preview</Label>
      <div className="mt-1 p-2 bg-zinc-900 rounded-lg border border-zinc-800 max-w-[120px]">
        {previewStyles[variant]}
      </div>
    </div>
  );
};

export default CardVariantSelector;
